import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../../lib/api';
import { TransactionVolumeData } from '../../types/api';

const VolumeChart: React.FC = () => {
  const { data: volumeData, isLoading, error } = useQuery({
    queryKey: ['volumeData'],
    queryFn: () => apiClient.getVolumeData(7),
    refetchInterval: 60000, // Refresh every minute
  });

  // Use mock data as fallback if API fails or is loading
  const mockData: TransactionVolumeData[] = [
  ];

  const displayData = volumeData && !error ? volumeData : mockData;

  const maxValue = Math.max(...displayData.flatMap((d: TransactionVolumeData) => [d.bscToRonin, d.roninToBsc]));

  const formatAmount = (amount: number) => {
    if (amount >= 1000000) return `${(amount / 1000000).toFixed(2)}M`;
    if (amount >= 1000) return `${(amount / 1000).toFixed(0)}K`;
    return amount.toString();
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-medium text-gray-900">Bridge Volume (7 Days)</h3>
          <p className="text-sm text-gray-500">
            Daily transaction volume between chains
            {isLoading && " (Loading...)"}
            {error && " (Using fallback data)"}
          </p>
        </div>
        <div className="flex items-center space-x-4">

          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-blue-500 rounded"></div>
            <span className="text-sm text-gray-600">Ronin → BSC</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-orange-500 rounded"></div>
            <span className="text-sm text-gray-600">BSC → Ronin</span>
          </div>
        </div>
      </div>
      
      {/* Simple Bar Chart */}
      <div className="space-y-4">
        {displayData.map((item: TransactionVolumeData, index: number) => (
          <div key={index} className="flex items-center space-x-4">
            <div className="w-16 text-xs text-gray-500 text-right">
              {formatDate(item.date)}
            </div>
            <div className="flex-1 relative">
              <div className="flex space-x-1 h-8">
                {/* Ronin to BSC bar */}
                <div className="relative flex-1 bg-gray-100 rounded">
                  <div
                    className="bg-blue-500 h-full rounded transition-all duration-300 hover:bg-blue-600"
                    style={{ width: `${(item.roninToBsc / maxValue) * 100}%` }}
                  ></div>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-xs text-white font-medium">
                      {formatAmount(item.roninToBsc)}
                    </span>
                  </div>
                </div>

                {/* BSC to Ronin bar */}
                <div className="relative flex-1 bg-gray-100 rounded">
                  <div
                    className="bg-orange-500 h-full rounded transition-all duration-300 hover:bg-orange-600"
                    style={{ width: `${(item.bscToRonin / maxValue) * 100}%` }}
                  ></div>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-xs text-white font-medium">
                      {formatAmount(item.bscToRonin)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div className="w-16 text-xs text-gray-500">
              {formatAmount(item.bscToRonin + item.roninToBsc)}
            </div>
          </div>
        ))}
      </div>
      
      {/* Summary Stats */}
      <div className="mt-6 pt-6 border-t border-gray-200">
        <div className="grid grid-cols-3 gap-4">
          <div className="text-center">
            <p className="text-2xl font-semibold text-gray-900">
              {formatAmount(displayData.reduce((sum: number, item: TransactionVolumeData) => sum + item.bscToRonin + item.roninToBsc, 0))}
            </p>
            <p className="text-sm text-gray-500">Total Volume</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-semibold text-blue-600">
              {formatAmount(displayData.reduce((sum: number, item: TransactionVolumeData) => sum + item.roninToBsc, 0))}
            </p>
            <p className="text-sm text-gray-500">Ronin → BSC</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-semibold text-orange-600">
              {formatAmount(displayData.reduce((sum: number, item: TransactionVolumeData) => sum + item.bscToRonin, 0))}
            </p>
            <p className="text-sm text-gray-500">BSC → Ronin</p>
          </div>
          
        </div>
      </div>
    </div>
  );
};

export default VolumeChart; 